import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useAuthStore from '../../store/auth';
import useSignupStore from '../../store/signup';
import Auth from '../../lib/Auth';
import shallow from 'zustand/shallow';
import LoadingBox from '../../components/layout/LoadingBox';
import logo from '../../assets/duett-logo.svg';
import { useLocation } from 'wouter';
import ax from '../../lib/api';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderLeft: `10px solid ${theme.palette.secondary.main}`,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: 240,
  },
  logo: {
    marginBottom: theme.spacing(8),
  },
  formControl: {
    marginBottom: 24,
  },
  submitButton: {
    marginBottom: 36,
    color: 'white',
  },
  input: {
    marginRight: 0,
  },
  helperText: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const Login = ({ notLoggedIn }) => {
  const classes = useStyles();
  const [, setLocation] = useLocation();
  const { isProfileCompleted } = useSignupStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState();
  const [login, checkingAuth, authenticated] = useAuthStore(
    (state) => [state.login, state.checkingAuth, state.authenticated],
    shallow
  );

  const {
    setAuthToken,
    setRefreshToken,
    setProfileData,
    setFundingSources,
    setServices,
    setCounties,
    setDocuments,
    setSignupStep,
    setProfileCompleted,
    setProfileSubmitted,
    clearSignupData,
  } = useSignupStore();

  const toggleForgotPassword = () => setShowForgotPassword(!showForgotPassword);

  useEffect(() => {
    const profileCompletedFromStorage = Auth.safeGetItem('isProfileCompleted');

    if (profileCompletedFromStorage || profileCompletedFromStorage === false) {
      setLocation('/signup');
    }
  }, [isProfileCompleted, setLocation]);

  async function checkUserSignupStatus(email, password) {
    try {
      const tokenResponse = await ax.post('/api/token/', { email, password });
      const { access, refresh } = tokenResponse.data;

      // Store token in both signupStore and Auth for API authentication
      setAuthToken(access);
      setRefreshToken(refresh);
      // Also store in Auth.accessToken so it can be used by API interceptor
      if (access) {
        Auth.accessToken = access;
        if (refresh) {
          Auth.refreshToken = refresh;
        }
      }
      localStorage.setItem('signup_email', email);
      localStorage.setItem('signup_password', password);

      // Try to check signup status, but if endpoint doesn't exist (404), skip it
      try {
        const statusResponse = await ax.post(
          '/api/users/check_user_signup_status/',
          { email, password }
        );
        const { status, step, data } = statusResponse.data;

        console.log('Step number received from backend:', step);

        if (status === 'Complete signup') {
          return true;
        } else if (status === 'Incomplete signup') {
          const profileData = {
            phone: data.provider_profile?.phone || '',
            first_name: data.provider_profile?.first_name || '',
            last_name: data.provider_profile?.last_name || '',
            company: data.provider_profile?.company || '',
            legalEntity: data.provider_profile?.legalEntity || '',
          };
          setProfileData(profileData);
          setFundingSources(data.funding_sources || []);
          setServices(data.services || []);
          setCounties(data.counties || []);
          setDocuments(data.upload_docs || []);

          setSignupStep(step === 0 || step === 1 ? 1 : step);

          setProfileCompleted(false);

          if (step === 0) {
            console.log('Step is 0, not setting isProfileSubmitted');
            localStorage.removeItem('isProfileSubmitted');
          } else if (step >= 1) {
            console.log('Step is >= 1, setting isProfileSubmitted to true');
            setProfileSubmitted(true);
          }

          window.location.reload();
          return false;
        }
      } catch (statusError) {
        // If signup status endpoint doesn't exist (404), just proceed with login
        // This is expected behavior - the endpoint may not exist in all backend configurations
        if (statusError.response?.status === 404) {
          // Silently skip - this is expected if endpoint doesn't exist
          return true;
        }
        // For other errors, log but proceed with login
        console.error('Error checking signup status:', statusError);
        return true;
      }

      // Default: proceed with login
      return true;
    } catch (error) {
      console.error('Error during token/signup check:', error);
      // If it's a network/CORS error, log more details
      if (!error.response) {
        console.error(
          'Network error - check if backend is running at:',
          process.env.REACT_APP_API_URL || 'NOT SET'
        );
        console.error('Error details:', error.message, error.code);
      }
      // Return true to proceed with login attempt even if signup check fails
      return true;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); // Clear previous errors

    if (showForgotPassword) {
      try {
        let res = await ax.post('/auth/password/reset/', {
          email: email,
        });
        if (res.status === 200) {
          setEmailSent(true);
        }
      } catch (e) {
        console.log('err', e.message);
        setError(
          e.response?.data?.message || e.message || 'Failed to send reset email'
        );
      }
    } else {
      try {
        const signupComplete = await checkUserSignupStatus(email, password);

        if (signupComplete) {
          // Don't clear tokens when clearing signup data - we need them for API calls
          const savedAccessToken = localStorage.getItem('signup_accessToken');
          const savedRefreshToken = localStorage.getItem('signup_refreshToken');

          clearSignupData();

          // Restore tokens after clearing (they're needed for authenticated API calls)
          if (savedAccessToken) {
            Auth.accessToken = savedAccessToken;
            setAuthToken(savedAccessToken);
          }
          if (savedRefreshToken) {
            Auth.refreshToken = savedRefreshToken;
            setRefreshToken(savedRefreshToken);
          }

          try {
            await login(email, password);
            // Small delay to ensure localStorage is fully written
            await new Promise((resolve) => setTimeout(resolve, 100));
            setLocation('/');
          } catch (loginError) {
            const errorMessage =
              loginError.response?.data?.detail ||
              loginError.response?.data?.message ||
              loginError.message ||
              'Login failed. Please try again.';
            setError(errorMessage);
          }
        }
      } catch (e) {
        const errorMessage =
          e.response?.data?.detail ||
          e.response?.data?.message ||
          e.message ||
          'Invalid username or password';
        setError(errorMessage);
      }
    }
  }

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit} className={classes.form}>
        <img src={logo} alt="Duett Logo" className={classes.logo} />

        <LoadingBox loading={checkingAuth}>
          {emailSent ? (
            <Typography className={classes.successMessage}>
              Password reset email has been sent.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column">
              <FormControl className={classes.formControl}>
                <Input
                  id="username-input"
                  placeholder="Email"
                  className={classes.input}
                  disableUnderline={true}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </FormControl>

              {!showForgotPassword && (
                <FormControl
                  className={classes.formControl}
                  style={{ marginBottom: 36 }}
                >
                  <Input
                    id="password-input"
                    type="password"
                    placeholder="Password"
                    className={classes.input}
                    disableUnderline={true}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <FormHelperText className={classes.helperText} error>
                    {error}
                  </FormHelperText>
                </FormControl>
              )}

              <Button
                variant="contained"
                color="secondary"
                disableElevation
                className={classes.submitButton}
                type={'submit'}
              >
                {showForgotPassword ? 'Submit' : 'Login'}
              </Button>

              <Button onClick={toggleForgotPassword} variant={'text'}>
                {showForgotPassword ? 'Back to Login' : 'Forgot Password?'}
              </Button>
            </Box>
          )}
        </LoadingBox>
      </form>
    </div>
  );
};

export default Login;
